<!-- eslint-disable
  vuejs-accessibility/mouse-events-have-key-events,
  vuejs-accessibility/no-static-element-interactions
-->
<template>
  <div
    ref="productCard"
    class="product-card stretched-link-container"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-image': showSecondaryImage,
      'product-card__inline': inline
    }"
    @focusin="loadSecondaryImage = true"
    @mouseenter="loadSecondaryImage = true"
  >
    <div class="product-card__top">
      <div class="product-card__image-container">
        <a
          v-if="cardType === 'product-cart-drawer'"
          class="product-card__image-link-cart-drawer"
          :href="product.url"
          :aria-label="product.title"
        />

        <responsive-image
          class="product-card__primary-image"
          :image="product.featuredImage"
          :lazyload="lazyload"
          :loaded-animation="loadedAnimation"
          ratio="4:5"
          :sizes="sizes"
        />

        <responsive-image
          v-if="loadSecondaryImage && product.secondaryImage"
          class="product-card__secondary-image"
          :image="product.secondaryImage"
          :padding="cardType === 'product-cart-drawer' ? true : false"
          ratio="4:5"
          :sizes="sizes"
          @image-loaded="showSecondaryImage = true"
        />

        <wishlist-button
          v-if="
            cardType === 'product-cart-drawer' &&
              product?.variants?.[0] &&
              product?.handle
          "
          class="product-card__wishlist product-card__wishlist--cart-drawer"
          :product="product"
          template="card"
          :variant="product.variants?.[0]"
        />
      </div>

      <product-form
        v-if="showQuickAdd && cardType !== 'product-cart-drawer' && !isMobile && product"
        class="product-card__form"
        add-to-cart-modifiers="primary center"
        is-product-card
        :one-per-order-enabled="onePerOrderEnabled"
        :product="product"
        :section-id="`product-card-${product.id}`"
        :settings="formSettings"
        :variant="product.variants[0]"
      />

      <product-badges
        v-if="showBadges && product?.badges?.length > 0"
        class="product-card__badges"
        :badges="product?.badges"
      />

      <wishlist-button
        v-if="showWishlistIcon && product?.variants?.[0] && product?.handle"
        class="product-card__wishlist product-card__wishlist--small"
        :product="product"
        template="card"
        :variant="product.variants?.[0]"
      />
    </div>

    <pre-order-message
      v-if="cardType === 'product-cart-drawer' && isMobile"
      :product="product"
    />

    <div class="product-card__inner">
      <div class="product-card__body">
        <p
          v-if="promoTag"
          class="
            product-card__promo-tag
            text-other-label-s
            text-other-label-l-desktop
          "
          :class="{
            'product-card__promo-tag--alt': product?.promoTagAltStyle
          }"
          v-text="promoTag"
        />

        <a
          class="
            product-card__title
            stretched-link
            text-body-s-regular
            text-body-m-regular-desktop
          "
          :href="product.url"
          @click="handleProductClick"
          @focus="loadSecondaryImage = true"
          v-html="product.title"
        />

        <product-prices
          class="product-card__prices"
          :compare-at-price="product?.compareAtPrice?.amount"
          :loading="!product"
          :price="product?.price?.amount"
          :price-first="false"
          :varies="product?.price?.varies"
        />

        <product-rating
          v-if="showRatings"
          class="product-card__rating"
          :product="product"
          :visible="visible"
        />

        <div v-if="cardType === 'product-cart-drawer' && !isMobile && product">
          <product-form
            add-to-cart-modifiers="secondary block center"
            is-product-card
            :product="product"
            :one-per-order-enabled="product?.limits?.onePerOrder"
            :section-id="`product-card-${product.id}`"
            :settings="formSettings"
            :variant="product.variants[0]"
          />
        </div>
      </div>

      <wishlist-button
        v-if="showWishlistIcon && product?.variants?.[0] && product?.handle"
        class="product-card__wishlist"
        :product="product"
        template="card"
        :variant="product.variants?.[0]"
      />
    </div>

    <div
      v-if="showQuickAdd && isMobile && product"
      class="product-card__footer"
    >
      <product-form
        class="product-card__form"
        add-to-cart-modifiers="secondary block center"
        is-product-card
        :product="product"
        :one-per-order-enabled="
          cardType === 'product-cart-drawer' ? product?.limits?.onePerOrder : onePerOrderEnabled
        "
        :section-id="`product-card-${product.id}`"
        :settings="formSettings"
        :variant="product.variants[0]"
      />
    </div>
  </div>

  <pre-order-message
    v-if="cardType === 'product-cart-drawer' && !isMobile"
    :product="product"
  />
</template>

<script>

/**
 * Vue: Product card (product-card)
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} [cardType] - Card type.
 * @param {Boolean} [lazyload] - Lazyload image, defaults to true.
 * @param {Boolean} [loadedAnimation] - Applies image loaded animation.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @parma {String} [sizes] - Image sizes.
 * @param {Boolean} [showWishlistIcon] - Shows wishlist icon.
 * @param {Boolean} [showRatings] - Shows ratings.
 * @param {Boolean} [showBadges] - Shows badges.
 * @param {Boolean} [showQuickAdd] - Shows quick add button.
 * @param {Boolean} [inline] - Applies styling for grid version.
 * @param {Boolean} [onePerOrderEnabled] - one per order enabled.
 *
 */
import { mapState } from 'vuex'

import PreOrderMessage from '~async/pre-order-message/pre-order-message'
import ProductBadges from '~async/product-badges/product-badges'
import ProductPrices from '~global/product-prices/product-prices'
import ProductRating from '~async/product-rating/product-rating'
import ProductForm from '~async/product-form/product-form'
import ResponsiveImage from '~global/images/responsive-image'
import WishlistButton from '~async/wishlist-button/wishlist-button'

import { setIntersectionObserver } from '~/helpers/general'
import { sendClickEvent } from '~/helpers/algolia-insights'

export default {
  name: 'ProductCard',

  components: {
    WishlistButton,
    PreOrderMessage,
    ProductBadges,
    ProductForm,
    ProductPrices,
    ProductRating,
    ResponsiveImage,
  },

  props: {
    cardType: {
      type: String,
      default: '',
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    loadedAnimation: {
      type: Boolean,
      default: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    sizes: {
      type: String,
      default: null,
    },
    showWishlistIcon: {
      type: Boolean,
      default: true,
    },
    showRatings: {
      type: Boolean,
      default: true,
    },
    showBadges: {
      type: Boolean,
      default: true,
    },
    showQuickAdd: {
      type: Boolean,
      default: true,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    onePerOrderEnabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      formSettings: {
        displayQuantitySelector: false,
      },
      loadSecondaryImage: false,
      showSecondaryImage: false,
      visible: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Get the additional promo-tag for a product.
     * - If the product is a Collectors Exclusive, at that string from
     *   theme-strings
     * - Promo tag data from product metafields
     * @returns {String}
     */
    promoTag() {
      if (this.product?.limits?.collectors) {
        return this.$string('product.collectors_exclusive_promo_tag')
      }

      if (this.product?.limits?.collectorsEarly) {
        return this.$string('product.collectors_early_promo_tag')
      }

      if (this.product?.promoTag) {
        return this.product.promoTag
      }

      return ''
    },

    /**
     * Compute whether to display the Collectors Exclusive CTA.
     * @returns {Boolean}
     */
    showCollectorsExclusiveCta() {
      return !this.$variable('customer.isCollector') && this.product?.limits?.collectors
    },
  },

  mounted() {

    /**
     * Set an intersection observer to check when the product card is nearly in
     * view.
     * - Used to fetch the product reviews.
     */
    setIntersectionObserver({
      callback: this.handleIntersection,
      element: this.$refs.productCard,
      rootMargin: '200px 100px',
      threshold: 0.05,
    })
  },

  methods: {

    /**
     * Check if the product card is intersecting and update the `visible` data.
     * - Observer is unobserved once visible to prevent multiple triggering.
     * @param entries - IntersectionObserver entries
     * @param observer - IntersectionObserver instance
     */
    handleIntersection(entries, observer) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        this.visible = true
        observer.unobserve(entry.target)
      })
    },

    /**
     * Handle product card click to send Algolia click event.
     * - Only fires when product has Algolia query context.
     * - Stores query in sessionStorage for PDP view/conversion tracking.
     * - Stores click data for Algolia app's built-in
     *   web pixel.
     */
    handleProductClick() {
      // eslint-disable-next-line no-underscore-dangle
      const queryID = this.product?.__queryID
      // eslint-disable-next-line no-underscore-dangle
      const indexName = this.product?.__indexName
      // eslint-disable-next-line no-underscore-dangle
      const position = this.product?.__position

      if (!queryID || !indexName) {
        return
      }

      sendClickEvent({
        indexName,
        objectID: this.product.variantId || this.product.objectID,
        position,
        queryID,
      })

      // Store Algolia context for the product page
      try {
        sessionStorage.setItem('algolia_query_context', JSON.stringify({
          queryID,
          indexName,
          objectID: String(this.product.variantId || this.product.objectID),
          position,
        }))
      } catch {
        // Silently fail if sessionStorage is unavailable
      }
    },
  },
}
</script>
